import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Какой пункт меню подсвечивать, когда путь страницы не принадлежит ни одному разделу.
 *
 * <p>Обычно раздел определяется по адресу: пункт активен на своём пути и его подстраницах. Но
 * карточка заявки перевозчика живёт по `/app/carrier/requests/:id`, а списки — по `/new`,
 * `/in-work` и `/done`: проваливаясь в заявку, перевозчик терял подсветку и переставал понимать,
 * где находится. Раздел здесь определяется не адресом, а состоянием заявки, поэтому его называет
 * сама страница.
 *
 * <p>Переопределение живёт ровно столько, сколько показана страница: при уходе оно снимается, и
 * подсветка снова считается по адресу.
 */
interface ActiveNavValue {
  readonly key: string | null;
  readonly setKey: (key: string | null) => void;
}

const ActiveNavContext = createContext<ActiveNavValue>({ key: null, setKey: () => {} });

export function ActiveNavProvider({ children }: { readonly children: ReactNode }) {
  const [key, setKey] = useState<string | null>(null);
  const value = useMemo(() => ({ key, setKey }), [key]);
  return <ActiveNavContext.Provider value={value}>{children}</ActiveNavContext.Provider>;
}

/** Ключ пункта, назначенного страницей; `null` — считать подсветку по адресу. */
export function useActiveNavKey(): string | null {
  return useContext(ActiveNavContext).key;
}

/**
 * Назначить активный пункт меню на время показа страницы. `null` (например, пока данные не
 * загрузились) оставляет подсветку по адресу — мигать подсветкой из-за ожидания ответа не нужно.
 */
export function useSetActiveNav(key: string | null): void {
  const { setKey } = useContext(ActiveNavContext);
  useEffect(() => {
    if (key == null) {
      return;
    }
    setKey(key);
    return () => setKey(null);
  }, [key, setKey]);
}
